/**
 * Micasa Cart API Interceptor
 * Intercepts all cart/add requests and enriches them with required line item properties
 *
 * Properties added:
 * - __language: Customer language (DE|FR|IT)
 * - __click_collect: From variant metafield
 * - __salutation: Customer salutation
 * - __atbit_configuration: For configurator products
 * - __borm_item_number: BORM item number
 * - __shipping_type: From variant metafield services
 * - __guest_order: Based on customer login status
 * - __variant_item_number: From variant metafield
 * - __product_item_number: From product metafield
 * - __assembly: Assembly service flag
 * - __disposal: Disposal service flag
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    propertyMockKey: 'property_mock',
    metaobjectNamespace: 'property_mock', // Used for metaobject references
    debug: false // Set to true for debugging
  };

  // Store original fetch to avoid conflicts with other interceptors
  const originalFetch = window.fetch;

  // No caching for property mock data - always fetch fresh values
  // This ensures property mock changes are immediately reflected

  // Logging helper
  const log = CONFIG.debug ? console.log.bind(console, '[Micasa Cart Props]') : () => {};

  /**
   * Get current customer language from Shopify locale or default to DE
   */
  function getCurrentLanguage() {
    // Check Shopify.locale first
    if (window.Shopify && window.Shopify.locale) {
      const locale = window.Shopify.locale.toUpperCase();
      if (['DE', 'FR', 'IT'].includes(locale)) {
        return locale;
      }
    }

    // Check document lang attribute
    const docLang = document.documentElement.lang?.split('-')[0]?.toUpperCase();
    if (['DE', 'FR', 'IT'].includes(docLang)) {
      return docLang;
    }

    // Default to DE
    return 'DE';
  }

  /**
   * Check if customer is logged in
   */
  function isCustomerLoggedIn() {
    // Check for customer account elements or Shopify customer object
    return !!(window.ShopifyAnalytics?.meta?.page?.customerId ||
        document.querySelector('.customer-logged-in') ||
        window.Shopify?.customer);
  }

  /**
   * Get salutation (temporarily hardcoded)
   * TODO: Implement proper customer salutation detection
   */
  function getCustomerSalutation() {
    // Temporarily return random salutation for testing
    return Math.random() > 0.5 ? 'Herr' : 'Frau';
  }

  /**
   * Fetch product and variant metafields using Storefront API
   */
  async function fetchProductMetafields(variantId) {
    // Don't cache property mock data to ensure fresh values
    // Property mock should always be fetched fresh for accuracy

    try {
      // Using the Storefront API to fetch metafields
      const query = `
        query getProductMetafields($variantId: ID!) {
          node(id: $variantId) {
            ... on ProductVariant {
              id
              storeAvailability(first: 10) {
                edges {
                  node {
                    available
                    pickUpTime
                    location {
                      name
                  }
                }
              }
            }

              product {
                propertyMock: metafield(namespace: "custom", key: "property_mock") {
                  value
                  type
                  reference {
                    ... on Metaobject {
                      fields {
                        key
                        value
                      }
                    }
                  }
                }
                productItemNumber: metafield(namespace: "micasa_app", key: "item_number") {
                  value
                }
              }
              clickAndCollect: metafield(namespace: "micasa_app", key: "click_and_collect") {
                value
              }
              services: metafield(namespace: "micasa_app", key: "services") {
                value
                type
              }
              variantItemNumber: metafield(namespace: "micasa_app", key: "item_number") {
                value
              }
            }
          }
        }
      `;

      const response = await fetch('/api/2024-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.Shopify?.storefrontAccessToken || ''
        },
        body: JSON.stringify({
          query,
          variables: {
            variantId: `gid://shopify/ProductVariant/${variantId}`
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch metafields: ${response.statusText}`);
      }

      const data = await response.json();
      log('Fetched metafields:', data); // Only log when debug is true
      // Map the aliased fields back to a consistent structure
      const product = data.data?.node?.product || {};
      const variant = data.data?.node || {};

      const result = {
        productMetafields: {
          propertyMock: product.propertyMock,
          itemNumber: product.productItemNumber
        },
        variantMetafields: {
          clickAndCollect: variant.clickAndCollect,
          services: variant.services,
          itemNumber: variant.variantItemNumber,
          storeAvailability: variant.storeAvailability
        }
      };

      // Don't cache property mock data
      // metafieldCache.set(cacheKey, {
      //   data: result,
      //   timestamp: Date.now()
      // });

      return result;
    } catch (error) {
      console.error('[Micasa Cart Props] Error fetching metafields:', error);
      return { productMetafields: {}, variantMetafields: {} };
    }
  }

  /**
   * Parse shipping type from services metafield
   */
  function parseShippingType(servicesMetafield) {
    if (!servicesMetafield?.value) return '';

    try {
      const services = typeof servicesMetafield.value === 'string'
          ? JSON.parse(servicesMetafield.value)
          : servicesMetafield.value;

      // Extract shipping type based on your business logic
      return services.shipping_type || services.default || '';
    } catch (error) {
      log('Error parsing shipping services:', error);
      return '';
    }
  }


  /**
   * Check Click & Collect availability from GraphQL or fallback to DOM
   */
  function isClickAndCollectAvailable(storeAvailability) {
    // GraphQL method (jetzt funktioniert es!)
    if (storeAvailability?.edges && storeAvailability.edges.length > 0) {
      const availableStores = storeAvailability.edges.filter(edge =>
          edge.node.available === true && edge.node.pickUpTime // pickUpTime indicates pickup is enabled
      );
      log('🏪 GraphQL stores with pickup available:', availableStores.length);
      log('🏪 GraphQL Click & Collect Available:', availableStores.length > 0);
      return availableStores.length > 0;
    }

    // Fallback to DOM method (falls GraphQL fehlschlägt)
    log('🏪 Using DOM fallback for Click & Collect detection');
    return checkClickAndCollectFromDOM();
  }

  /**
   * Check if click and collect is available using DOM elements as fallback
   */
  function checkClickAndCollectFromDOM() {
    const pickupElement = document.querySelector('pickup-availability[available]');
    const isPickupAvailable = !!pickupElement;

    log('🏪 DOM Pickup Availability Element found:', !!pickupElement);
    log('🏪 DOM Click & Collect Available:', isPickupAvailable);

    return isPickupAvailable;
  }

  /**
   * Process property mock metaobject reference
   */
  function processPropertyMock(propertyMockMetafield) {
    if (!propertyMockMetafield) {
      return null;
    }

    // Check if it has a reference with fields (metaobject)
    if (propertyMockMetafield.reference?.fields) {
      const fields = propertyMockMetafield.reference.fields;
      const mockData = {};

      fields.forEach(field => {
        // Skip the 'title' field as it's just for display
        if (field.key === 'title' || field.key === 'Title') {
          return;
        }

        // The fields come without __ prefix, so we need to add it
        // Map field names to the expected property names
        const propertyKey = `__${field.key}`;
        mockData[propertyKey] = field.value;
      });

      log('Property mock data from metaobject:', mockData);
      return mockData;
    }

    // Fallback: try to parse value as JSON if it's a string
    if (propertyMockMetafield.value) {
      try {
        const parsed = typeof propertyMockMetafield.value === 'string'
            ? JSON.parse(propertyMockMetafield.value)
            : propertyMockMetafield.value;
        log('Property mock data from value:', parsed);
        return parsed;
      } catch (e) {
        log('Failed to parse property mock value:', e);
      }
    }

    return null;
  }

  /**
   * Enrich cart item with required properties
   */
  async function enrichCartItem(formData, variantId) {
    const properties = {};

    // Add language
    formData.append(`attributes[__language]`, getCurrentLanguage());

    // Add guest order status
    formData.append(`attributes[__guest_order]`, !isCustomerLoggedIn() ? 'true' : 'false');

    // Add salutation (temporarily hardcoded)
    formData.append(`attributes[__salutation]`, getCustomerSalutation());

    // Add default values
    properties['__atbit_configuration'] = '';
    properties['__borm_item_number'] = '';
    // properties['__assembly'] = 'false';
    // properties['__disposal'] = 'false';

    // Fetch and add metafield-based properties
    try {
      const { productMetafields, variantMetafields } = await fetchProductMetafields(variantId);

      // Get the property mock metafield directly (it's now aliased)
      const propertyMockField = productMetafields.propertyMock || null;

      // Process property mock if available - these values take PRIORITY
      const propertyMock = processPropertyMock(propertyMockField);
      if (propertyMock) {
        log('Property mock found! Overriding with:', propertyMock);
        // Property mock values override ALL other values
        Object.keys(propertyMock).forEach(key => {
          if (key.startsWith('__')) {
            properties[key] = propertyMock[key];
            log(`Override: ${key} = ${propertyMock[key]}`);
          }
        });
      }


// Check Click & Collect availability from store availability
      const storeAvailability = variantMetafields?.storeAvailability;
      const clickCollectAvailable = isClickAndCollectAvailable(storeAvailability);
      log(`🏪 Click & Collect available for variant ${variantId}:`, clickCollectAvailable);

// Add click and collect (only if not overridden by property mock)
      if (!propertyMock || !propertyMock.hasOwnProperty('__click_collect')) {
        // Use store availability to determine click & collect
        properties['__click_collect'] = clickCollectAvailable ? 'true' : 'false';
        log(`🎯 Setting __click_collect to:`, properties['__click_collect']);
      } else {
        log(`⚡ Property mock overrides click_collect to:`, propertyMock['__click_collect']);
      }

      // Add shipping type (only if not overridden by property mock)
      if (!propertyMock || !propertyMock.hasOwnProperty('__shipping_type')) {
        const servicesField = variantMetafields.services;
        const shippingType = parseShippingType(servicesField);
        // properties['__shipping_type'] = shippingType;
      }

      // Add item numbers (only if not overridden by property mock)
      if (!propertyMock || !propertyMock.hasOwnProperty('__variant_item_number')) {
        const variantItemNumber = variantMetafields.itemNumber;
        // properties['__variant_item_number'] = variantItemNumber?.value || '';
      }

      if (!propertyMock || !propertyMock.hasOwnProperty('__product_item_number')) {
        const productItemNumber = productMetafields.itemNumber;
        // properties['__product_item_number'] = productItemNumber?.value || '';
      }

    } catch (error) {
      log('Error enriching cart item:', error);
    }

    // Add properties to FormData
    Object.keys(properties).forEach(key => {
      formData.append(`properties[${key}]`, properties[key]);
    });

    return formData;
  }

  /**
   * Intercept fetch requests to enrich cart items with required properties
   */
  window.fetch = async function(...args) {
    const [url, config] = args;

    // Process all cart/add requests
    if (typeof url === 'string' && url.includes('/cart/add')) {
      log('Processing cart/add request');

      // Handle FormData body
      if (config?.body instanceof FormData) {
        const variantId = config.body.get('id');
        if (variantId) {
          try {
            config.body = await enrichCartItem(config.body, variantId);
            log('Cart item enriched with properties');
          } catch (error) {
            console.error('[Micasa Cart Props] Error enriching cart request:', error);
          }
        }
      }

      // Handle JSON body
      else if (config?.body && typeof config.body === 'string') {
        try {
          const bodyData = JSON.parse(config.body);

          if (bodyData.id) {
            // Create FormData from JSON
            const formData = new FormData();
            Object.keys(bodyData).forEach(key => {
              if (key !== 'properties' && key !== 'attributes') {
                formData.append(key, bodyData[key]);
              }
            });

            // Add existing properties if any
            if (bodyData.properties) {
              Object.keys(bodyData.properties).forEach(key => {
                formData.append(`properties[${key}]`, bodyData.properties[key]);
              });
            }

            // Add existing attributes if any
            if (bodyData.attributes) {
              Object.keys(bodyData.attributes).forEach(key => {
                formData.append(`attributes[${key}]`, bodyData.attributes[key]);
              });
            }

            // Enrich with new properties
            const enrichedFormData = await enrichCartItem(formData, bodyData.id);

            // Convert back to JSON
            const enrichedData = { ...bodyData, properties: {}, attributes: {} };
            for (let [key, value] of enrichedFormData.entries()) {
              if (key.startsWith('properties[')) {
                const propKey = key.match(/properties\[(.+)\]/)[1];
                enrichedData.properties[propKey] = value;
              } else if (key.startsWith('attributes[')) {
                const attrKey = key.match(/attributes\[(.+)\]/)[1];
                enrichedData.attributes[attrKey] = value;
              } else if (key !== 'id' && key !== 'quantity') {
                enrichedData[key] = value;
              }
            }

            config.body = JSON.stringify(enrichedData);
            log('Cart item enriched with properties (JSON)');
          }
        } catch (error) {
          console.error('[Micasa Cart Props] Error parsing/enriching JSON body:', error);
        }
      }
    }

    // Call the original fetch
    return originalFetch.apply(this, args);
  };

  /**
   * Alternative: Intercept XMLHttpRequest for older implementations
   */
  const originalXHROpen = XMLHttpRequest.prototype.open;
  const originalXHRSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function(method, url, ...args) {
    this._url = url;
    this._method = method;
    return originalXHROpen.apply(this, [method, url, ...args]);
  };

  XMLHttpRequest.prototype.send = function(body) {
    if (this._url && this._url.includes('/cart/add')) {
      console.log('Intercepting XMLHttpRequest cart/add');
      // Handle XMLHttpRequest interception if needed
      // This would require similar logic to the fetch interceptor
    }
    return originalXHRSend.apply(this, [body]);
  };

  /**
   * Initialize the interceptor
   */
  function init() {
    // Check if already initialized
    if (window.MicasaCartPropsInterceptor) {
      console.log('[Micasa Cart Props] Already initialized');
      return;
    }

    console.log('[Micasa Cart Props] Interceptor initialized (Storefront API version)');

    // Listen for custom cart events if your theme uses them
    document.addEventListener('cart:add', async (event) => {
      log('Cart add event detected', event.detail);
    });

    // Monitor form submissions for product forms
    document.addEventListener('submit', async (event) => {
      const form = event.target;
      if (form.matches('form[action*="/cart/add"]') || form.matches('[is="product-form"]')) {
        log('Product form submission detected');

        // Get variant ID from form
        const variantInput = form.querySelector('[name="id"]');
        if (variantInput?.value) {
          // Pre-fetch metafields to warm the cache
          if (CONFIG.debug) {
            fetchProductMetafields(variantInput.value).catch(e => log('Pre-fetch error:', e));
          }
        }
      }
    }, true);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Export for debugging
  window.MicasaCartPropsInterceptor = {
    getCurrentLanguage,
    isCustomerLoggedIn,
    getCustomerSalutation,
    fetchProductMetafields,
    enrichCartItem,
    processPropertyMock,
    CONFIG,
    // Test function to check property generation for a variant
    testVariant: async function(variantId) {
      // Force debug mode for testing
      const originalDebug = CONFIG.debug;
      CONFIG.debug = true;

      console.log('Testing variant:', variantId);

      try {
        // First fetch and show raw metafields
        const metafields = await fetchProductMetafields(variantId);
        console.log('Raw metafields response:', metafields);

        // Check Click & Collect availability from store availability
        const storeAvailability = metafields.variantMetafields?.storeAvailability;
        const clickCollectAvailable = isClickAndCollectAvailable(storeAvailability);
        log(`🏪 Click & Collect available for variant ${variantId}:`, clickCollectAvailable);

        // Process property mock if exists
        if (metafields.productMetafields?.propertyMock) {
          const processed = processPropertyMock(metafields.productMetafields.propertyMock);
          console.log('Processed property mock:', processed);
        }

        // Now test the full enrichment
        const formData = new FormData();
        formData.append('id', variantId);
        const enriched = await enrichCartItem(formData, variantId);
        const props = {};
        for (let [key, value] of enriched.entries()) {
          if (key.startsWith('properties[')) {
            const propKey = key.match(/properties\[(.+)\]/)[1];
            props[propKey] = value;
          }
        }
        console.table(props);
        return props;
      } finally {
        // Restore original debug setting
        CONFIG.debug = originalDebug;
      }
    }
  };
})();